import { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiPlus, FiCopy, FiTrash2 } from "react-icons/fi";
import { Scenario } from "../../../api/scenarios";

interface ScenarioSelectorProps {
  scenarios: Scenario[];
  selectedScenarioId: string | null;
  onSelect: (scenarioId: string) => void;
  onCreate: () => void;
  onClone?: (scenarioId: string) => void;
  onDelete?: (scenarioId: string) => void;
}

const statusDot = (status: string | null) => {
  if (!status) return "bg-gray-500";
  if (status === "completed") return "bg-green-400";
  if (status === "running" || status === "queued") return "bg-yellow-400 animate-pulse";
  if (status === "failed") return "bg-red-400";
  return "bg-gray-500";
};

export function ScenarioSelector({
  scenarios,
  selectedScenarioId,
  onSelect,
  onCreate,
  onClone,
  onDelete,
}: ScenarioSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = scenarios.find((s) => s.scenario_id === selectedScenarioId);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-border bg-surface-600 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-500 transition-colors"
      >
        {selected && (
          <span className={`inline-block w-2 h-2 rounded-full ${statusDot(selected.latest_run_status)}`} />
        )}
        <span>{selected?.scenario_name || "Select Scenario"}</span>
        {selected?.is_default && (
          <span className="text-xs text-text-secondary">(default)</span>
        )}
        <FiChevronDown className="text-xs text-text-secondary" />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-72 rounded-lg border border-border bg-surface-600 shadow-xl z-30 overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {scenarios.map((s) => (
              <div
                key={s.scenario_id}
                className={`flex items-center justify-between px-4 py-2.5 hover:bg-surface-500 cursor-pointer transition-colors ${
                  s.scenario_id === selectedScenarioId
                    ? "bg-surface-500 border-l-2 border-quantum-400"
                    : ""
                }`}
              >
                <button
                  onClick={() => {
                    onSelect(s.scenario_id);
                    setIsOpen(false);
                  }}
                  className="flex items-center gap-2 flex-1 text-left"
                >
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${statusDot(s.latest_run_status)}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {s.scenario_name}
                    </div>
                    {s.nameplate_capacity && (
                      <div className="text-xs text-text-secondary">
                        {s.nameplate_capacity} MW
                        {s.battery_flag && " + Battery"}
                        {s.ppa_flag && " + PPA"}
                      </div>
                    )}
                  </div>
                </button>

                {!s.is_default && (
                  <div className="flex items-center gap-1 ml-2">
                    {onClone && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onClone(s.scenario_id);
                          setIsOpen(false);
                        }}
                        className="p-1 text-text-secondary hover:text-quantum-400 transition-colors"
                        title="Clone scenario"
                      >
                        <FiCopy className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(s.scenario_id);
                          setIsOpen(false);
                        }}
                        className="p-1 text-text-secondary hover:text-health-400 transition-colors"
                        title="Delete scenario"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-border">
            <button
              onClick={() => {
                onCreate();
                setIsOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-quantum-400 hover:bg-surface-500 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Add Scenario
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
